import { IconNames } from '@blueprintjs/icons';
import { GridWithFilters, PrimaryData } from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { ListPageHeader } from 'components/ListPageHeader';
import { CREATE_ROUTE } from 'services/constants';
import { useGet } from 'services/Http';
import { getPayerMap, getPayerSegmentMap } from 'services/PayerSegmentSupport';

const columns = ({ navigate }) => [
    {
        header: 'Payer Market Name',
        accessorKey: 'payer_market_name',
        size: 200,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        header: 'Payer',
        accessorKey: 'payerName',
        size: 120,
    },
    {
        header: 'Payer Segments',
        accessorKey: 'segments',
        size: 250,
    },
    {
        header: 'Actions',
        accessorKey: 'actions',
        size: 70,
        enableSorting: false,
        cell: ({ row }) => (
            <div className="sds_filter-list__actions">
                <Icon
                    icon={IconNames.EDIT}
                    intent={Icon.Intent.SECONDARY}
                    title="Edit"
                    onClick={() =>
                        navigate({
                            pathname: `/payer-market/${row.original.payer_market_id}`,
                        })
                    }
                />
            </div>
        ),
    },
];

export const PayerMarketList = () => {
    const navigate = useNavigate();
    const payerMarkets = useGet('/reference/payer_markets');
    const payers = useGet('/reference/payers/');
    const payerMap = getPayerMap(payers);
    const payerSegments = useGet('/reference/payer_segments');
    const payerSegmentMap = getPayerSegmentMap(payerSegments);

    const data = useMemo(
        () =>
            payerMarkets?.map((payerMarket) => ({
                ...payerMarket,
                payerName: payerMap?.[payerMarket.payer_id],
                segments: payerMarket.segment_ids
                    ?.map((segmentId) => payerSegmentMap?.[segmentId]?.segment_name ?? segmentId)
                    .join(', '),
            })),
        [payerMarkets, payerMap, payerSegmentMap],
    );

    return (
        <div className="sds_filter-list">
            <GridWithFilters
                headerContent={
                    <>
                        <ListPageHeader
                            pageTitle="Payer Markets"
                            buttons={[
                                {
                                    label: 'Create Payer Market',
                                    path: `/payer-market/${CREATE_ROUTE}`,
                                },
                            ]}
                        />
                        <div className="margin__bottom--medium">
                            Payer Markets allow FMH to combine one or more payer segments for a more
                            refined market rate calculation.
                        </div>
                    </>
                }
                enableSearch
                isLoading={!(payerMarkets && payers)}
                data={data}
                sortProperties={[{ id: 'payer_market_name', desc: false }]}
                filterCategories={[
                    {
                        key: 'payerName',
                        title: 'Payer',
                        showCounts: true,
                    },
                ]}
                columns={columns({ navigate })}
            />
        </div>
    );
};
